import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { arcadeApi, arcadeAuth } from '../../services/arcade';
import { RegisterGate } from '../../components/arcade/RegisterGate';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const FEEDBACK_CLASS: Record<string, string> = {
  correct: 'bg-green-500 text-white border-green-500',
  present: 'bg-yellow-400 text-white border-yellow-400',
  absent: 'bg-gray-300 text-gray-600 border-gray-300',
};

interface GuessRow {
  guess: string;
  feedback: string[];
}

export const CryptoWordPage: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(arcadeAuth.isLoggedIn());
  const [state, setState] = useState<any | null>(null);
  const [rows, setRows] = useState<GuessRow[]>([]);
  const [input, setInput] = useState('');
  const [finished, setFinished] = useState(false);
  const [finalInfo, setFinalInfo] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    arcadeApi
      .wordToday()
      .then((data) => {
        setState(data);
        if (data.attempt) {
          // reconstruímos as linhas anteriores sem feedback detalhado; o
          // essencial (terminou/acertou) vem do backend
          setRows(data.attempt.guesses.map((g: string) => ({ guess: g, feedback: [] })));
          if (data.attempt.finished) {
            setFinished(true);
            setFinalInfo({ solved: data.attempt.solved, word: data.word, explanation: data.explanation });
          }
        }
      })
      .catch(() => setState({ available: false }))
      .finally(() => setLoading(false));
  }, [loggedIn]);

  const submitGuess = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const guess = input.toLowerCase().trim();
    if (!state || guess.length !== state.length) {
      setError(`A palavra tem ${state?.length} letras.`);
      return;
    }
    try {
      const data = await arcadeApi.wordGuess(guess);
      setRows((prev) => [...prev, { guess, feedback: data.feedback }]);
      setInput('');
      if (data.finished) {
        setFinished(true);
        setFinalInfo(data);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao enviar tentativa.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Crypto Word: a Palavra Cripto do Dia | Crypto Arcade | {SITE_NAME}</title>
        <meta
          name="description"
          content="Descubra a palavra do dia do universo cripto em até 6 tentativas, com dica e explicação do termo ao final. Jogo diário educativo e gratuito."
        />
        <link rel="canonical" href={`${SITE_URL}/arcade/crypto-word`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/arcade" className="hover:text-blue-600 transition">Arcade</Link>
            <span>›</span>
            <span className="text-gray-700">Crypto Word</span>
          </nav>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">📝 Crypto Word</h1>
          <p className="text-gray-600 mb-6">
            Uma palavra do universo cripto por dia. Até 6 tentativas — verde = letra certa no lugar certo,
            amarelo = letra existe em outra posição.
          </p>

          {!loggedIn ? (
            <RegisterGate onRegistered={() => setLoggedIn(true)} />
          ) : loading ? (
            <div className="text-gray-500 text-center py-10">Carregando...</div>
          ) : !state?.available ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-600">
              A palavra de hoje ainda está sendo preparada. Volte em alguns minutos!
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-6 md:p-8">
              <p className="text-sm text-gray-600 mb-4">
                💡 <strong>Dica:</strong> {state.hint}
              </p>

              {/* Tentativas */}
              <div className="space-y-2 mb-5">
                {rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex gap-1.5 justify-center">
                    {row.guess.split('').map((letter, letterIndex) => (
                      <span
                        key={letterIndex}
                        className={`w-10 h-10 md:w-11 md:h-11 border-2 rounded flex items-center justify-center font-bold uppercase ${
                          row.feedback[letterIndex]
                            ? FEEDBACK_CLASS[row.feedback[letterIndex]]
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                      >
                        {letter}
                      </span>
                    ))}
                  </div>
                ))}
                {!finished &&
                  Array.from({ length: (state.maxTries || 6) - rows.length }).map((_, emptyIndex) => (
                    <div key={`empty-${emptyIndex}`} className="flex gap-1.5 justify-center">
                      {Array.from({ length: state.length }).map((_, cellIndex) => (
                        <span
                          key={cellIndex}
                          className="w-10 h-10 md:w-11 md:h-11 border-2 border-gray-200 rounded"
                        />
                      ))}
                    </div>
                  ))}
              </div>

              {finished && finalInfo ? (
                <div className="text-center">
                  <p className="text-3xl mb-2">{finalInfo.solved ? '🎉' : '😅'}</p>
                  <p className="font-bold text-gray-900 mb-1">
                    {finalInfo.solved ? 'Você acertou!' : 'Não foi dessa vez.'}
                  </p>
                  <p className="text-gray-700 mb-3">
                    A palavra era: <strong className="uppercase">{finalInfo.word}</strong>
                  </p>
                  {finalInfo.points > 0 && (
                    <p className="text-sm text-green-700 font-semibold mb-3">+{finalInfo.points} NC</p>
                  )}
                  {finalInfo.explanation && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-left text-sm text-blue-900 leading-relaxed mb-4">
                      📚 {finalInfo.explanation}
                    </div>
                  )}
                  <Link to="/arcade" className="text-blue-600 hover:underline text-sm font-semibold">
                    ← Voltar ao Arcade
                  </Link>
                </div>
              ) : (
                <form onSubmit={submitGuess} className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value.replace(/[^a-zA-Z]/g, ''))}
                    maxLength={state.length}
                    placeholder={`Palavra de ${state.length} letras`}
                    className="flex-grow border border-gray-300 rounded-lg px-4 py-2.5 text-sm uppercase tracking-widest focus:outline-none focus:border-blue-500"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white font-semibold rounded-lg px-6 py-2.5 hover:bg-blue-700 transition"
                  >
                    Tentar
                  </button>
                </form>
              )}
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
